import { useEffect, useMemo, useState } from "react";

import WriteOpsHint from "../components/WriteOpsHint";
import { fetchDeploymentConfig } from "../lib/api";
import { describeApiError } from "../lib/apiError";
import type { ConfigEntry, DeploymentConfig } from "../lib/types";
import { SYSTEM_WRITE_OPS } from "../writeops/system";

/**
 * Deployment-wide settings for a realm knob this UI *does* edit — so the operator
 * is not left reading "it is off here" and hunting for the switch at the wrong
 * level. The YAML value stays read-only; the per-realm override is the editable one.
 */
const REALM_OVERRIDE_HINTS: Record<string, string> = {
  "workspaces.enabled": "per-realm override: Realm → Workspace",
  "surfaces.human_views_default": "per-realm override: Realm → Workspace",
  "maintenance.grounding_inference_mode": "per-realm override: Realm → Workspace",
  "llm.default_chat_provider": "per-realm override: Realm → Models",
  "llm.embedding_provider": "per-realm override: Realm → Models",
};

function renderValue(entry: ConfigEntry): string {
  if (entry.is_secret_ref) return typeof entry.value === "string" ? entry.value : "***";
  if (entry.value === null || entry.value === undefined) return "—";
  if (typeof entry.value === "string") return entry.value;
  return JSON.stringify(entry.value);
}

function isDefault(entry: ConfigEntry): boolean {
  if (entry.required) return false;
  return JSON.stringify(entry.value) === JSON.stringify(entry.default);
}

function groupOf(path: string): string {
  const dot = path.indexOf(".");
  return dot === -1 ? path : path.slice(0, dot);
}

function ConfigRow({ entry }: { entry: ConfigEntry }) {
  const override = REALM_OVERRIDE_HINTS[entry.path];
  return (
    <tr className="border-t border-kw-border-subtle align-top">
      <td className="py-1.5 pr-4 font-mono text-kw-text-muted whitespace-nowrap">
        {entry.path}
      </td>
      <td className="py-1.5 pr-4 font-mono text-kw-text-primary break-all">
        {renderValue(entry)}
        {entry.is_secret_ref && (
          <span className="ml-2 text-kw-text-faint">
            {entry.secret_present ? "(present)" : "(missing)"}
          </span>
        )}
      </td>
      <td className="py-1.5 pr-4 whitespace-nowrap">
        {isDefault(entry) && <span className="text-kw-text-faint">default</span>}
      </td>
      <td className="py-1.5 text-kw-text-faint">
        {entry.description}
        {override && (
          <span className="block text-kw-l1">{override}</span>
        )}
      </td>
    </tr>
  );
}

/**
 * A view, not a form — and it has to look like one. No editable control renders
 * here beyond the filter: a greyed-out input reads as "you could, if you were
 * allowed" and provokes exactly the question this screen answers. What it does
 * offer is *where* each knob is turned.
 */
export default function DeploymentConfigRoute() {
  const [config, setConfig] = useState<DeploymentConfig | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let live = true;
    fetchDeploymentConfig()
      .then((c) => {
        if (live) setConfig(c);
      })
      .catch((e: unknown) => {
        if (live) setError(describeApiError(e).message);
      });
    return () => {
      live = false;
    };
  }, []);

  const groups = useMemo(() => {
    if (!config) return [];
    const needle = filter.trim().toLowerCase();
    const matching = needle
      ? config.entries.filter((e) => e.path.toLowerCase().includes(needle))
      : config.entries;
    const byGroup = new Map<string, ConfigEntry[]>();
    for (const entry of matching) {
      const group = groupOf(entry.path);
      const bucket = byGroup.get(group);
      if (bucket) bucket.push(entry);
      else byGroup.set(group, [entry]);
    }
    return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [config, filter]);

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <h2 className="text-lg font-medium">Deployment Config</h2>
        <p className="text-kw-text-muted text-sm mt-1">
          The <strong>effective</strong> boot-time configuration of this instance — read-only.
          Values are loaded once at startup and never mutated at runtime, so changing one means
          editing the deployment YAML and restarting.
        </p>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {config && (
        <>
          <div className="flex flex-wrap items-center gap-4 text-xs text-kw-text-muted">
            <span>
              <span className="text-kw-text-faint">version</span>{" "}
              <span className="font-mono">{config.kw_version}</span>
            </span>
            <span>
              <span className="text-kw-text-faint">profile</span>{" "}
              <span className="font-mono">{config.deployment_profile}</span>
            </span>
            <span>
              <span className="text-kw-text-faint">source</span>{" "}
              <span className="font-mono">{config.config_file}</span>
            </span>
            <span>
              <span className="text-kw-text-faint">entries</span> {config.entry_count}
            </span>
          </div>

          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter by path, e.g. llm. or maintenance.op3"
            className="w-full max-w-md text-xs bg-kw-bg-elevated rounded p-2 text-kw-text-primary border border-kw-border-subtle focus:outline-none focus:border-kw-l1"
          />

          {groups.length === 0 && (
            <div className="text-xs text-kw-text-faint">No entry matches that filter.</div>
          )}

          {groups.map(([group, entries]) => (
            <section
              key={group}
              className="border border-kw-border-subtle rounded bg-kw-bg-elevated/40"
            >
              <header className="px-4 py-2 border-b border-kw-border-subtle flex items-baseline gap-2">
                <h3 className="text-sm font-mono text-kw-text-primary">{group}</h3>
                <span className="text-xs text-kw-text-faint">{entries.length}</span>
              </header>
              <div className="px-4 py-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {entries.map((entry) => (
                      <ConfigRow key={entry.path} entry={entry} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <div className="text-xs text-kw-text-faint border border-kw-border-subtle rounded p-3 space-y-1">
            <div>
              Secret values are never served — a <span className="font-mono">${"{VAR}"}</span>{" "}
              reference plus whether it resolves is all this endpoint knows.
            </div>
            <div>
              A deployment may deep-merge a local override file, so this is the effective value,
              not the content of one particular file.
            </div>
          </div>

          <WriteOpsHint title="Apply a config change" ops={SYSTEM_WRITE_OPS} />
        </>
      )}
    </div>
  );
}
